/**
 * useReceiptBlob — Fetches a receipt's authenticated thumbnail/file as a Blob URL.
 *
 * Why blob URLs?
 *   <img src="https://api/receipts/:id/thumbnail"> issues a cross-origin GET that
 *   does NOT include credentials by default, so the request is unauthenticated and
 *   the browser silently fails with a 401. Fetching via axios (which has
 *   `withCredentials: true`) and converting the response to an `object URL`
 *   guarantees the file is loaded with auth and rendered offline.
 *   Ref: https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
 *
 * For PDFs we return `{ url: null }` immediately — the caller renders a static
 * PDF icon. Embedding PDFs via blob URL into <embed>/<object> can break inline
 * viewers in some browsers (Round-2 §6 deferral).
 */
import { useEffect, useRef, useState } from 'react';
import api, { endpoints } from '@/services/api';

export interface UseReceiptBlobOptions {
  /** Defer fetching until the `ref` element intersects the viewport. */
  lazy?: boolean;
  /** IntersectionObserver root margin. Defaults to '200px'. */
  rootMargin?: string;
}

export interface UseReceiptBlobResult {
  url: string | null;
  loading: boolean;
  error: boolean;
  ref: React.RefObject<HTMLElement>;
}

export function useReceiptBlob(
  receiptId: string,
  mimeType: string,
  opts: UseReceiptBlobOptions = {}
): UseReceiptBlobResult {
  const { lazy = false, rootMargin = '200px' } = opts;
  const isPdf = mimeType === 'application/pdf';

  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(!lazy);
  const ref = useRef<HTMLElement>(null);

  // IntersectionObserver — only when lazy
  useEffect(() => {
    if (!lazy || isPdf || shouldFetch) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldFetch(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [lazy, isPdf, shouldFetch, rootMargin]);

  // Fetch blob — image MIME types only
  useEffect(() => {
    if (isPdf || !shouldFetch || !receiptId) return;

    const controller = new AbortController();
    let createdUrl: string | null = null;
    let cancelled = false;

    setLoading(true);
    setError(false);

    api
      .get(endpoints.receipts.thumbnail(receiptId), {
        responseType: 'blob',
        signal: controller.signal,
      })
      .then((res) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(res.data as Blob);
        setUrl(createdUrl);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // Axios v1 surfaces aborts as `CanceledError` — ignore them.
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [isPdf, shouldFetch, receiptId]);

  return { url: isPdf ? null : url, loading, error, ref };
}

export default useReceiptBlob;
